import { Group, Stack, Text } from "@mantine/core";
import { Badge } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { Change, ChangeType } from "@models/releaseNotes";
import { useLocalized } from "@utils/localizedText";

const TONE: Record<ChangeType, string> = {
  new: "green",
  improved: "blue",
  fixed: "gray",
};

interface Props {
  changes: Change[];
}

/**
 * Renders a release's changes as plain text with a typed badge.
 *
 * No markdown renderer and no `dangerouslySetInnerHTML`. This content is mounted
 * inside the shell, which hosts every other module, so an HTML sink here would
 * be the worst-placed one in the platform.
 */
export const ChangeList = ({ changes }: Props) => {
  const { t } = useTranslation();
  const { pick } = useLocalized();

  return (
    <Stack gap="xs">
      {changes.map((change, index) => (
        <Group key={index} gap="sm" align="flex-start" wrap="nowrap">
          {/* Badge already defaults to the light variant. */}
          <Badge color={TONE[change.type]} size="sm" style={{ flexShrink: 0 }}>
            {t(`releases.changeType.${change.type}`)}
          </Badge>
          <Text size="sm">{pick(change.text)}</Text>
        </Group>
      ))}
    </Stack>
  );
};
